import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface EmptyStateProps {
  icon: LucideIcon;
  title: string;
  description?: string;
  /** Optional CTA rendered below the description (e.g. a Button). */
  action?: ReactNode;
  /** Tighter paddings for sheets/drawers and inline sections. */
  compact?: boolean;
  className?: string;
}

/**
 * Shared empty state: icon-in-circle, title, supporting copy, optional CTA.
 * Mirrors the pattern established in post-list.tsx so every empty surface
 * (feed tabs, sheets, profile tabs, event tabs) looks consistent.
 */
export function EmptyState({
  icon: Icon,
  title,
  description,
  action,
  compact = false,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center text-center",
        compact ? "px-6 py-8" : "px-6 py-12",
        className,
      )}
    >
      <div
        className={cn(
          "rounded-full bg-surface-alt",
          compact ? "mb-3 p-3" : "mb-4 p-4",
        )}
      >
        <Icon
          className={cn("text-text-secondary", compact ? "h-6 w-6" : "h-8 w-8")}
        />
      </div>
      <h3 className="mb-1 text-sm font-medium text-text-primary">{title}</h3>
      {description ? (
        <p className="max-w-sm text-[13px] leading-snug text-text-secondary">
          {description}
        </p>
      ) : null}
      {action ? <div className="mt-4">{action}</div> : null}
    </div>
  );
}
